import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { formatRelative } from "@/lib/format";
import { Plus, X, Loader2, Save, Trash2, Globe, RefreshCw, AlertCircle, CheckCircle2, BookOpen } from "lucide-react";
import { KNOWLEDGE_CATEGORIES, type KnowledgeCategory } from "@ai-leasing-os/shared";
import { cn } from "@/lib/cn";
import { useToast } from "@/lib/toast";
import { EmptyState } from "@/components/ui/empty-state";

type TabKey = "documents" | "sources";

interface ListRow {
  id: string;
  title: string;
  category: KnowledgeCategory;
  priority: number;
  is_active: number;
  updated_at: string;
  body_preview: string;
}

interface FullDoc extends Omit<ListRow, "body_preview"> {
  body: string;
  tags: string[] | null;
}

export function KnowledgePage() {
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState<TabKey>("documents");
  // Initial filter is read from the URL (?category=…) so deep links from
  // Business Brain land on a pre-filtered list. KNOWLEDGE_CATEGORIES is the
  // single source of truth - unknown values fall back to null.
  const initialCategory = (() => {
    const raw = searchParams.get("category");
    if (raw && (KNOWLEDGE_CATEGORIES as readonly string[]).includes(raw)) {
      return raw as KnowledgeCategory;
    }
    return null;
  })();
  const [categoryFilter, setCategoryFilter] = useState<KnowledgeCategory | null>(initialCategory);
  const [showInactive, setShowInactive] = useState(false);
  const [editingId, setEditingId] = useState<string | "new" | null>(null);

  // Keep the URL in sync when the user toggles the filter, so the back/forward
  // navigation and "share this view" both work.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (categoryFilter) next.set("category", categoryFilter);
    else next.delete("category");
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryFilter]);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["knowledge", categoryFilter, showInactive],
    queryFn: () => {
      const params = new URLSearchParams({ limit: "200" });
      if (categoryFilter) params.set("category", categoryFilter);
      if (!showInactive) params.set("is_active", "true");
      return api.get<ListRow[]>(`/api/knowledge?${params.toString()}`);
    },
  });

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Knowledge Base</h1>
          <p className="text-sm text-slate-500">
            Business rules the AI uses to ground its replies (payment terms, viewing rules, scripts, FAQs).
          </p>
        </div>
        {tab === "documents" && (
          <Button onClick={() => setEditingId("new")}>
            <Plus className="h-4 w-4" /> New document
          </Button>
        )}
      </header>

      <div className="flex gap-1 border-b border-slate-200 -mb-px">
        <TabButton active={tab === "documents"} onClick={() => setTab("documents")}>
          📝 Documents
        </TabButton>
        <TabButton active={tab === "sources"} onClick={() => setTab("sources")}>
          🌐 Auto sources
        </TabButton>
      </div>

      {tab === "sources" && <SourcesPanel />}

      {tab === "documents" && <>
      <Card className="p-3 flex flex-wrap items-center gap-2">
        <span className="text-[10px] uppercase tracking-wider text-slate-400 mr-1">category</span>
        <button
          onClick={() => setCategoryFilter(null)}
          className={cn("px-2 py-0.5 rounded text-[11px] font-medium",
            categoryFilter === null ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200",
          )}
        >
          all
        </button>
        {KNOWLEDGE_CATEGORIES.map((c) => (
          <button
            key={c}
            onClick={() => setCategoryFilter(c === categoryFilter ? null : c)}
            className={cn("px-2 py-0.5 rounded text-[11px] font-medium",
              categoryFilter === c ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200",
            )}
          >
            {c}
          </button>
        ))}
        <label className="ml-3 flex items-center gap-1.5 text-xs text-slate-600">
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
          show inactive
        </label>
        <span className="ml-auto text-xs text-slate-500">{rows.length} docs</span>
      </Card>

      <Card>
        {rows.length === 0 ? (
          isLoading ? (
            <div className="p-12 text-center text-sm text-slate-400">Loading…</div>
          ) : categoryFilter ? (
            <EmptyState
              icon={<BookOpen className="h-6 w-6" />}
              title="No documents in this category"
              description="Clear the category filter to see all knowledge documents."
              action={{ label: "Clear filter", onClick: () => setCategoryFilter(null) }}
            />
          ) : (
            <EmptyState
              icon={<BookOpen className="h-6 w-6" />}
              title="No knowledge documents yet"
              description="Add documents (payment terms, viewing rules, FAQs) so the assistant answers from your real business rules instead of guessing."
              action={{
                label: "New document",
                onClick: () => setEditingId("new"),
                icon: <Plus className="h-4 w-4" />,
              }}
            />
          )
        ) : (
          <ul className="divide-y divide-slate-100">
            {rows.map((d) => (
              <li
                key={d.id}
                onClick={() => setEditingId(d.id)}
                className={`flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-slate-50 ${
                  d.is_active === 0 ? "opacity-50" : ""
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium text-slate-900 truncate">{d.title}</h3>
                    <Badge variant="muted">{d.category}</Badge>
                    <Badge variant="info">priority {d.priority}</Badge>
                    {d.is_active === 0 && <Badge variant="danger">inactive</Badge>}
                  </div>
                  <p className="text-xs text-slate-500 mt-1 line-clamp-2">{d.body_preview}</p>
                </div>
                <span className="text-[10px] text-slate-400 whitespace-nowrap">{formatRelative(d.updated_at)}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {editingId && (
        <KnowledgeEditDrawer
          docId={editingId === "new" ? null : editingId}
          onClose={() => setEditingId(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ["knowledge"] });
            setEditingId(null);
          }}
        />
      )}
      </>}
    </div>
  );
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "px-4 py-2 text-sm border-b-2 transition-colors",
        active
          ? "border-slate-900 text-slate-900 font-medium"
          : "border-transparent text-slate-500 hover:text-slate-900",
      )}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// SourcesPanel - auto-ingested URLs (websites). Each scrape generates
// knowledge_documents rows tagged source_type='website'.
// ---------------------------------------------------------------------------

interface SourceRow {
  id: string;
  source_type: string;
  url: string;
  label: string | null;
  status: string;
  doc_count: number;
  last_scraped_at: string | null;
  last_error: string | null;
  refresh_interval_hours: number;
  is_active: number;
  created_at: string;
  updated_at: string;
}

function SourcesPanel() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data: sources = [], isLoading } = useQuery({
    queryKey: ["knowledge", "sources"],
    queryFn: () => api.get<SourceRow[]>("/api/knowledge/sources"),
    refetchInterval: 15_000,
  });

  const [url, setUrl] = useState("");
  const [category, setCategory] = useState<KnowledgeCategory>("faq");
  const [label, setLabel] = useState("");
  const [lastResult, setLastResult] = useState<{ ok: boolean; detail: string } | null>(null);

  const scrapeMut = useMutation({
    mutationFn: async () => {
      const res = await api.post<{ url: string; doc_count: number; error: string | null }>(
        "/api/knowledge/sources",
        { url: url.trim(), category, label: label.trim() || undefined },
      );
      return res;
    },
    onSuccess: (res) => {
      setLastResult({
        ok: res.error === null,
        detail: res.error ?? `${res.doc_count} sections indexed from ${res.url}`,
      });
      if (res.error === null) {
        setUrl("");
        setLabel("");
        toast.success("Website scraped", `${res.doc_count} section${res.doc_count > 1 ? "s" : ""} indexed`);
      } else {
        toast.error("Scrape failed", res.error);
      }
      qc.invalidateQueries({ queryKey: ["knowledge"] });
    },
    onError: (e) => {
      const msg = e instanceof Error ? e.message : String(e);
      setLastResult({ ok: false, detail: msg });
      toast.error("Scrape failed", msg);
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.del(`/api/knowledge/sources/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["knowledge"] });
      toast.success("Source removed");
    },
    onError: (e) => toast.error("Delete failed", e instanceof Error ? e.message : String(e)),
  });

  const rescrapeMut = useMutation({
    mutationFn: (sourceUrl: string) =>
      api.post<unknown>("/api/knowledge/sources", { url: sourceUrl, category: "faq" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["knowledge"] });
      toast.success("Re-scraped");
    },
    onError: (e) => toast.error("Re-scrape failed", e instanceof Error ? e.message : String(e)),
  });

  return (
    <div className="space-y-4">
      <Card className="p-4 space-y-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
          <Globe className="h-4 w-4" />
          Add a website to scrape
        </div>
        <p className="text-xs text-slate-500">
          Paste a public page URL. The worker fetches it, splits each <code>&lt;h1&gt;</code> / <code>&lt;h2&gt;</code> / <code>&lt;h3&gt;</code> section into a knowledge document, and the AI uses them just like manual docs.
          Re-scrapes happen automatically every 24h.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
          <Input
            className="md:col-span-6"
            placeholder="https://your-domain.com/about"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && url.trim().length > 0) {
                e.preventDefault();
                scrapeMut.mutate();
              }
            }}
          />
          <select
            className="md:col-span-3 h-9 rounded-md border border-slate-300 px-2 text-sm bg-brand-surface"
            value={category}
            onChange={(e) => setCategory(e.target.value as KnowledgeCategory)}
          >
            {KNOWLEDGE_CATEGORIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <Input
            className="md:col-span-3"
            placeholder="Label (optional)"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-3">
          <Button
            type="button"
            size="sm"
            onClick={() => scrapeMut.mutate()}
            disabled={url.trim().length === 0 || scrapeMut.isPending}
          >
            {scrapeMut.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Scraping…
              </>
            ) : (
              <>
                <Globe className="h-4 w-4" /> Scrape & index
              </>
            )}
          </Button>
          {lastResult && (
            <div className={cn(
              "text-xs px-2 py-1 rounded flex items-center gap-1",
              lastResult.ok ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700",
            )}>
              {lastResult.ok ? <CheckCircle2 className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />}
              {lastResult.detail}
            </div>
          )}
        </div>
      </Card>

      <Card>
        <div className="p-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-900">Registered sources</h3>
          <span className="text-xs text-slate-500">{sources.length}</span>
        </div>
        {isLoading ? (
          <div className="p-8 text-center"><Loader2 className="h-4 w-4 animate-spin text-slate-400 mx-auto" /></div>
        ) : sources.length === 0 ? (
          <div className="p-8 text-center text-xs text-slate-400">
            No sources yet. Paste a URL above to start.
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {sources.map((s) => (
              <li key={s.id} className="px-4 py-3 flex items-start gap-3">
                <SourceStatusBadge status={s.status} />
                <div className="flex-1 min-w-0">
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm font-medium text-slate-900 hover:underline truncate block"
                  >
                    {s.label || s.url}
                  </a>
                  <div className="text-[11px] text-slate-500 truncate">{s.url}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5 flex flex-wrap items-center gap-2">
                    <span>{s.doc_count} sections</span>
                    {s.last_scraped_at && <span>· last scrape {formatRelative(s.last_scraped_at)}</span>}
                    <span>· auto-refresh every {s.refresh_interval_hours}h</span>
                  </div>
                  {s.last_error && (
                    <div className="text-[11px] text-red-700 bg-red-50 mt-1 px-2 py-1 rounded">
                      {s.last_error}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    title="Re-scrape now"
                    onClick={() => rescrapeMut.mutate(s.url)}
                    disabled={rescrapeMut.isPending}
                  >
                    <RefreshCw className={cn("h-4 w-4", rescrapeMut.isPending && "animate-spin")} />
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    title="Delete source + its documents"
                    onClick={() => {
                      if (confirm(`Delete this source and all its ${s.doc_count} indexed sections?`)) {
                        deleteMut.mutate(s.id);
                      }
                    }}
                    disabled={deleteMut.isPending}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function SourceStatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; cls: string }> = {
    pending: { label: "Pending", cls: "bg-slate-100 text-slate-700" },
    success: { label: "OK", cls: "bg-green-50 text-green-700" },
    error:   { label: "Error", cls: "bg-red-50 text-red-700" },
  };
  const m = map[status] ?? { label: status, cls: "bg-slate-100 text-slate-700" };
  return (
    <span className={cn("text-[10px] font-medium px-2 py-1 rounded shrink-0", m.cls)}>
      {m.label}
    </span>
  );
}

function KnowledgeEditDrawer({
  docId,
  onClose,
  onSaved,
}: {
  docId: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = docId === null;
  const [form, setForm] = useState<Partial<FullDoc>>(isNew ? { title: "", category: "faq", body: "", priority: 5, is_active: 1 } : {});

  const { data, isLoading } = useQuery({
    queryKey: ["knowledge", docId],
    queryFn: () => api.get<FullDoc>(`/api/knowledge/${docId}`),
    enabled: !isNew,
  });

  const doc: Partial<FullDoc> = isNew ? form : { ...data, ...form };

  const saveMut = useMutation({
    mutationFn: () => {
      const payload: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(form)) {
        if (v !== undefined) {
          // `is_active` is stored as `0 | 1` in the FullDoc union; coerce
          // to a boolean for the API payload. The DB writes it as TINYINT.
          if (k === "is_active") payload[k] = v === 1;
          else payload[k] = v;
        }
      }
      if (isNew) return api.post<{ id: string }>("/api/knowledge", payload);
      return api.put<{ id: string }>(`/api/knowledge/${docId}`, payload);
    },
    onSuccess: () => onSaved(),
  });

  const deleteMut = useMutation({
    mutationFn: () => api.del(`/api/knowledge/${docId}`),
    onSuccess: () => onSaved(),
  });

  function setField<K extends keyof FullDoc>(k: K, v: FullDoc[K] | null) {
    setForm({ ...form, [k]: v as never });
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/40">
      <div className="w-full max-w-3xl bg-brand-surface shadow-xl overflow-y-auto">
        <header className="sticky top-0 bg-brand-surface flex items-center justify-between border-b border-slate-200 px-5 py-3 z-10">
          <h2 className="font-semibold">{isNew ? "New knowledge document" : `Edit · ${doc.title ?? ""}`}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X className="h-5 w-5" />
          </button>
        </header>

        {isLoading && !isNew ? (
          <div className="p-12 text-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400 mx-auto" /></div>
        ) : (
          <div className="p-5 space-y-3">
            <Field label="Title *">
              <Input value={String(doc.title ?? "")} onChange={(e) => setField("title", e.target.value)} />
            </Field>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Category *">
                <select
                  className="h-9 w-full rounded-md border border-slate-300 px-2 text-sm"
                  value={String(doc.category ?? "faq")}
                  onChange={(e) => setField("category", e.target.value as KnowledgeCategory)}
                >
                  {KNOWLEDGE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Priority (1-10)">
                <Input type="number" min="1" max="10"
                  value={String(doc.priority ?? 5)}
                  onChange={(e) => setField("priority", Number(e.target.value))} />
              </Field>
              <Field label="Active">
                <div className="flex items-center h-9">
                  <input
                    type="checkbox"
                    checked={doc.is_active === 1}
                    onChange={(e) => setField("is_active", (e.target.checked ? 1 : 0) as never)}
                  />
                  <span className="ml-2 text-sm text-slate-600">used by AI</span>
                </div>
              </Field>
            </div>
            <Field label="Body (Markdown supported)">
              <textarea
                rows={16}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-mono leading-relaxed"
                value={String(doc.body ?? "")}
                onChange={(e) => setField("body", e.target.value)}
              />
            </Field>

            <div className="border-t border-slate-200 pt-3 flex justify-between">
              {!isNew ? (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => {
                    if (
                      confirm(
                        "Deactivate this document? The assistant will stop using it to answer leads. You can re-activate it later.",
                      )
                    )
                      deleteMut.mutate();
                  }}
                  disabled={deleteMut.isPending}
                >
                  <Trash2 className="h-4 w-4" />
                  Deactivate
                </Button>
              ) : <span />}
              <div className="flex gap-2">
                <Button variant="ghost" onClick={onClose}>Cancel</Button>
                <Button onClick={() => saveMut.mutate()} disabled={saveMut.isPending}>
                  {saveMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Save
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="text-xs font-medium text-slate-700">{label}</span>
      {children}
    </label>
  );
}
